import { motion } from 'framer-motion';
import { shuffle } from 'lodash';
import React, { memo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { Images } from '../../../constants/images';
import { getDistractorPools } from '../../../constants/MemoryGames/distractorPool';
import { SVGIconType } from '../../../constants/svgIcon';
import { TtsLocale, Uuid } from '../../../models/Base';
import {
  GameController,
  GamePlayState,
} from '../../../models/GameControllerStore';
import {
  TextOrderingAnswer,
  TextOrderingUnit,
  TextOrderingUnitType,
} from '../../../models/GameStore';
import { TextEvaluationWord } from '../../../models/RecordStore';
import { getCharacterCount } from '../../../utils/string';
import { PngIcon, SvgIcon } from '../../Molecules';
import {
  AnswerCard,
  AnswerCardStatus,
  GameBlankAnswerCardsGroup,
} from '../GameBlankAnswerCardsGroup';
import { GameQuestionPanel, TextOrderingGameMode } from '../GameQuestionPanel';

import styles from './MemoryGameMainGroup.module.css';

export type BlankInfo = {
  score: number;
  pickedAnswer?: string;
};

type Unit = TextOrderingUnit & {
  wordIndex: number;
  unitIndex: number;
  wordTtsAudio?: Uuid;
};

type MemoryGameStoreState = {
  gameController: GameController | undefined;
  blankUnits: Unit[] | undefined;
  mode: TextOrderingGameMode;
  locale: TtsLocale;
  isPinyin: boolean;
  correctAnswersPerHand: number;
  currentBlankIndex: number;
  isEnded: boolean;
  blankInfos: Record<number, BlankInfo>;
  cards: AnswerCard[];
  hintUsed: boolean;
  setEvaluation: ((blankInfos: Record<number, BlankInfo>) => void) | undefined;
};

type MemoryGameStoreAction = {
  setup: (
    gameController: GameController | undefined,
    answer: TextOrderingAnswer,
  ) => Promise<void>;
  cleanup: () => void;
  refreshCards: () => void;
  onHint: () => void;
  onSwap: () => boolean;
  onAnswer: (pickedCard: AnswerCard) => boolean;
  updateCardStatus: (card: AnswerCard, newStatus: AnswerCardStatus) => void;
};

type Store = MemoryGameStoreState & MemoryGameStoreAction;

const INITIAL_VALUE: MemoryGameStoreState = {
  gameController: undefined,
  blankUnits: undefined,
  mode: TextOrderingGameMode.QuickMemory,
  locale: TtsLocale.US,
  isPinyin: false,
  correctAnswersPerHand: 1,
  currentBlankIndex: -1,
  isEnded: false,
  blankInfos: {},
  cards: [],
  hintUsed: false,
  setEvaluation: undefined,
};

const useMemoryGameStore = create<Store>((set, get) => {
  const onEnd = () => {
    set({ isEnded: true });
    get()
      .gameController?.setGamePlayState(GamePlayState.Score)
      .catch(console.error);
  };

  return {
    ...INITIAL_VALUE,
    //
    setup: async (
      gameController: GameController | undefined,
      answer: TextOrderingAnswer,
    ) => {
      const blankUnits = answer.words
        .flatMap((word, wordIndex) =>
          word.units.map((unit, unitIndex) => ({
            ...unit,
            wordIndex,
            unitIndex,
            wordTtsAudio: word.ttsAudio,
          })),
        )
        .filter((unit) => unit.type === TextOrderingUnitType.Blank);

      let mode: TextOrderingGameMode;
      if (answer.answerFirstLetter) {
        mode = TextOrderingGameMode.QuickMemory;
      } else if (blankUnits.some((unit) => getCharacterCount(unit.key) > 1)) {
        mode = TextOrderingGameMode.Syllable;
      } else {
        mode = TextOrderingGameMode.Spelling;
      }

      let correctAnswersPerHand: number;
      if (blankUnits.length <= 1) {
        correctAnswersPerHand = 1;
      } else if (blankUnits.length <= 3) {
        correctAnswersPerHand = 2;
      } else if (blankUnits.length <= 4) {
        correctAnswersPerHand = 2 + Math.round(Math.random());
      } else if (blankUnits.length <= 6) {
        correctAnswersPerHand = 3 + Math.round(Math.random());
      } else if (blankUnits.length <= 8) {
        correctAnswersPerHand = 4 + Math.round(Math.random());
      } else if (blankUnits.length <= 10) {
        correctAnswersPerHand = 5 + Math.round(Math.random());
      } else {
        correctAnswersPerHand = 6 + Math.round(Math.random());
      }

      const setEvaluation = (blankInfos: Record<number, BlankInfo>) => {
        const evaluation = answer.words.map(
          (word) =>
            new TextEvaluationWord({
              word: word.word,
              records: [
                {
                  units: word.units.map((unit) => ({
                    unit: unit.key,
                    isBlank: unit.type === TextOrderingUnitType.Blank,
                  })),
                },
              ],
            }),
        );

        Object.entries(blankInfos).forEach(([blankIndex, blankInfo]) => {
          const unit = blankUnits[blankIndex as unknown as number];
          if (unit) {
            evaluation[unit.wordIndex]?.records[0]?.units[
              unit.unitIndex
            ]?.setScore(blankInfo.score);
          }
        });

        gameController?.setInAppEvaluation(evaluation);
      };

      set({
        gameController,
        blankUnits,
        locale: answer.locale,
        mode,
        isPinyin: false, // TODO
        correctAnswersPerHand,
        currentBlankIndex: 0,
        isEnded: false,
        blankInfos: {},
        cards: [],
        hintUsed: false,
        setEvaluation,
      });

      gameController?.startStage(blankUnits.length);
      setEvaluation({});
      await gameController?.setGamePlayState(GamePlayState.Run);
      await gameController?.setGamePlayState(GamePlayState.Running);
      gameController?.playAudio(blankUnits[0]?.wordTtsAudio);
    },
    cleanup: () => {
      set(INITIAL_VALUE);
    },
    refreshCards: () => {
      const {
        blankUnits,
        mode,
        locale,
        isPinyin,
        correctAnswersPerHand,
        currentBlankIndex,
      } = get();
      if (blankUnits) {
        const currentBlankUnit = blankUnits[currentBlankIndex];
        if (currentBlankUnit) {
          let cardValues: string[];
          if (mode === TextOrderingGameMode.Syllable) {
            cardValues = generateSyllableCards(blankUnits);
          } else {
            const correctAnswers = blankUnits
              .slice(
                currentBlankIndex,
                currentBlankIndex + correctAnswersPerHand,
              )
              .map((unit) => unit.key[0] as string);

            cardValues = generateLetterCards(
              correctAnswers,
              12,
              locale,
              isPinyin,
            );
          }
          set({
            cards: shuffle(
              cardValues.map((value) => ({
                value,
                status: AnswerCardStatus.Pending,
              })),
            ),
          });
        }
      }
    },
    onHint: () => {
      const {
        gameController,
        blankUnits,
        mode,
        currentBlankIndex,
        cards,
        hintUsed,
      } = get();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];

      if (!hintUsed && currentBlankUnit) {
        const expectedAnswer =
          mode === TextOrderingGameMode.QuickMemory
            ? currentBlankUnit.key[0]
            : currentBlankUnit.key;

        const indexes: number[] = [];
        cards.map((card, i) => {
          if (card.value === expectedAnswer) {
            indexes.push(i);
          }
        });
        const index = indexes[Math.floor(Math.random() * indexes.length)];

        const updatedCards = cards.map((card, i) =>
          i === index || Math.random() < 0.5
            ? card
            : {
                ...card,
                status:
                  card.status === AnswerCardStatus.Pending
                    ? AnswerCardStatus.HintEliminate
                    : card.status,
              },
        );
        set({ cards: updatedCards, hintUsed: true });

        if (gameController?.addScore(-5) === 0) {
          onEnd();
        }
      }
    },
    onSwap: () => {
      const {
        gameController,
        blankUnits,
        mode,
        currentBlankIndex,
        blankInfos,
        cards,
        setEvaluation,
      } = get();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];
      const currentAnswer =
        mode === TextOrderingGameMode.QuickMemory
          ? currentBlankUnit?.key[0]
          : currentBlankUnit?.key;

      const hasCorrectAnswers = cards.some(
        (card) =>
          card.status === AnswerCardStatus.Pending &&
          card.value === currentAnswer,
      );
      if (hasCorrectAnswers) {
        const currentInfo = blankInfos[currentBlankIndex] ?? {
          score: 100,
        };
        const newBlankInfos = {
          ...blankInfos,
          [currentBlankIndex]: {
            score: Math.max(currentInfo.score - 30, 0),
          },
        };
        set({ blankInfos: newBlankInfos });
        setEvaluation?.(newBlankInfos);

        if (gameController?.addScore(-15) === 0) {
          onEnd();
        }
      } else {
        const answerCount = blankUnits?.length ?? 0;
        const baseScore = answerCount === 0 ? 0 : Math.ceil(60 / answerCount);

        set({ cards: [] });
        gameController?.addScore(baseScore);
      }

      return !hasCorrectAnswers;
    },
    onAnswer: (pickedCard: AnswerCard) => {
      const {
        gameController,
        blankUnits,
        mode,
        currentBlankIndex,
        blankInfos,
        setEvaluation,
        updateCardStatus,
      } = get();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];
      const currentAnswer =
        mode === TextOrderingGameMode.QuickMemory
          ? currentBlankUnit?.key[0]
          : currentBlankUnit?.key;

      const isCorrect =
        Boolean(currentAnswer) && pickedCard.value === currentAnswer;

      updateCardStatus(
        pickedCard,
        isCorrect ? AnswerCardStatus.Correct : AnswerCardStatus.Wrong,
      );

      const currentInfo = blankInfos[currentBlankIndex] ?? {
        score: 100,
      };
      const newBlankInfos = {
        ...blankInfos,
        [currentBlankIndex]: isCorrect
          ? {
              score: currentInfo.score,
              pickedAnswer: pickedCard.value,
            }
          : {
              score: Math.max(currentInfo.score - 30, 0),
            },
      };
      set({ blankInfos: newBlankInfos });
      setEvaluation?.(newBlankInfos);

      const answerCount = blankUnits?.length ?? 0;
      const baseScore = answerCount === 0 ? 0 : Math.ceil(60 / answerCount);

      if (isCorrect) {
        const nextIndex = currentBlankIndex + 1;
        set({
          currentBlankIndex: nextIndex,
          hintUsed: false,
        });

        gameController?.addScore(baseScore);
        gameController?.addStagePoint(1);

        if (nextIndex < answerCount) {
          gameController?.initializeNewLap(Infinity);

          if (
            blankUnits?.[currentBlankIndex]?.wordTtsAudio !==
            blankUnits?.[nextIndex]?.wordTtsAudio
          ) {
            gameController?.playAudio(blankUnits?.[nextIndex]?.wordTtsAudio);
          }
        } else {
          onEnd();
        }
      } else if (gameController?.addScore(-baseScore * 2) === 0) {
        onEnd();
      }

      return isCorrect;
    },
    updateCardStatus: (card: AnswerCard, newStatus: AnswerCardStatus) => {
      const { cards } = get();
      const updatedCards = cards.map((c) =>
        c === card ? { ...c, status: newStatus } : c,
      );
      set({ cards: updatedCards });
    },
  };
});

export type MemoryGameMainGroupProps = {
  gameController?: GameController;
  answer: TextOrderingAnswer;
  isAssistedGame?: boolean;
};

export const MemoryGameMainGroup = memo(
  ({ gameController, answer, isAssistedGame }: MemoryGameMainGroupProps) => {
    const {
      blankUnits,
      mode,
      currentBlankIndex,
      isEnded,
      blankInfos,
      cards,
      hintUsed,
      setup,
      cleanup,
      refreshCards,
      onHint,
      onSwap,
      onAnswer,
      updateCardStatus,
    } = useMemoryGameStore();
    const { t } = useTranslation();

    useEffect(() => {
      if (!blankUnits) {
        setup(gameController, answer).catch(console.error);
      }
    }, [gameController, answer, blankUnits, setup]);

    useEffect(() => {
      return cleanup;
    }, [cleanup]);

    const currentBlankUnit = blankUnits?.[currentBlankIndex];

    useEffect(() => {
      if (cards.length === 0 && currentBlankUnit && !isEnded) {
        refreshCards();
      }
    }, [cards, currentBlankUnit, isEnded, refreshCards]);

    if (!blankUnits) {
      return <></>;
    }

    if (blankUnits && !blankUnits.length) {
      return <div>Error: Game question data is undefined or empty.</div>;
    }

    let tutorialCard: AnswerCard | undefined;
    if (currentBlankUnit && isAssistedGame) {
      tutorialCard = cards.find((card) => {
        const expectedAnswer =
          mode === TextOrderingGameMode.QuickMemory
            ? currentBlankUnit.key[0]
            : currentBlankUnit.key;
        return (
          card.value === expectedAnswer &&
          card.status === AnswerCardStatus.Pending
        );
      });
    }

    const handleCardClick = (card: AnswerCard) => {
      const isCorrect = onAnswer(card);
      if (!isCorrect) {
        toast.error(t(`Incorrect! Please try again!`), {
          duration: 1200,
        });
      }
    };

    const handleSwapClick = () => {
      const isCorrect = onSwap();
      if (!isCorrect) {
        toast.error(t(`Still has correct answer!`), {
          duration: 1200,
        });
      }
    };

    return (
      <div className={styles.container}>
        <GameQuestionPanel
          answer={answer}
          currentBlankIndex={currentBlankIndex}
          blankInfos={blankInfos}
          mode={mode}
        />
        <div className={styles.answerCards}>
          {!isEnded && currentBlankUnit && (
            <GameBlankAnswerCardsGroup
              cards={cards}
              onCardClick={handleCardClick}
              onUpdateCardStatus={updateCardStatus}
              tutorialCard={tutorialCard}
            />
          )}
        </div>

        <div className={styles.buttonBar}>
          <div
            className={styles.back}
            onClick={() => {
              history.back();
            }}
          >
            <SvgIcon
              name={SVGIconType.Logout}
              width={28}
              height={28}
              fill={'white'}
              stroke="white"
            />
          </div>
          {!isEnded && mode !== TextOrderingGameMode.Syllable && (
            <>
              <HintButton onClick={onHint} disabled={hintUsed} scoreCost={5} />
              <SwapCardsButton
                onClick={handleSwapClick}
                showTutorial={Boolean(
                  currentBlankUnit && isAssistedGame && !tutorialCard,
                )}
              />
            </>
          )}
        </div>
      </div>
    );
  },
);

const HintButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  scoreCost: number;
}> = ({ onClick, disabled, scoreCost = 5 }) => {
  const { t } = useTranslation();
  return (
    <div
      className={!disabled ? styles.hint : `${styles.hint} ${styles.disabled}`}
      onClick={!disabled ? onClick : undefined}
    >
      <div className={styles.icon}>
        <img src={Images.JOYAJOY_logo} width={28} height={28} />
      </div>
      <div className={styles.text}>
        <span className={styles.label}>{t('Hint')}</span>
        <span className={styles.scoreDeduction}>-{scoreCost}</span>
      </div>
    </div>
  );
};

const SwapCardsButton: React.FC<{
  onClick: () => void;
  showTutorial: boolean;
}> = ({ onClick, showTutorial }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.swap} onClick={onClick}>
      <div className={styles.icon}>
        <PngIcon name="GameSwap" width={32} height={32} />
        {showTutorial && (
          <motion.img
            src={Images.HandwriteFinger}
            alt="Finger pointing"
            className={styles.fingerOverlay}
            initial={{ x: 10, y: 10 }}
            animate={{ x: [4, -4], y: [4, -4] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        )}
      </div>

      <span className={styles.label}>{t('Swap Cards')}</span>
    </div>
  );
};

// move to const
export const generateSyllableCards = (units: Unit[]) => {
  const cards: string[] = [];

  units.forEach((unit) => {
    if (unit.type === TextOrderingUnitType.Blank) {
      cards.push(unit.key);
    }
  });

  return cards;
};

export const generateLetterCards = (
  correctAnswers: string[],
  count: number,
  locale: TtsLocale,
  isPinyin: boolean,
) => {
  const distractorPools = getDistractorPools(locale);
  const confusion1 = ['1', 'i', 'I', 'l', 'L'];
  const confusion2 = ['0', 'o', 'O'];

  let exclusions: string[] = [];

  // remove confusion answers
  const answers: string[] = [];
  for (let i = 0; i < correctAnswers.length; i += 1) {
    const answer = correctAnswers[i] as string;
    if (exclusions.includes(answer)) {
      break;
    }

    answers.push(answer);

    if (confusion1.includes(answer)) {
      exclusions = exclusions.concat(confusion1.filter((c) => c !== answer));
    }
    if (confusion2.includes(answer)) {
      exclusions = exclusions.concat(confusion2.filter((c) => c !== answer));
    }
  }

  // create distractor pools
  let pool: string[] = [];

  answers.forEach((answer) => {
    // pool should also contains the correct answers
    pool.push(answer);

    // use pool if contains answer first
    const matchingPool = distractorPools.find((p) => p.pool.includes(answer));
    if (matchingPool) {
      pool = pool.concat(matchingPool.pool);
    } else {
      // if no pool contains answer, then use pools matching isPinyin with mustContains = false.
      // if no such pool, then use pools matching isPinyin
      // if no such pool, then use all pools
      // let pools = distractorPools.filter(
      //   (p) => p.isPinyin === isPinyin && !p.mustContains,
      // );
      // if (pools.length === 0) {
      //   pools = distractorPools.filter((p) => p.isPinyin);
      // }
      // if (pools.length === 0) {
      //   pools = distractorPools;
      // }
      // pool = pool.concat(...pools.map((p) => p.pool));
    }
  });

  if (pool.length <= answers.length) {
    let pools = distractorPools.filter(
      (p) => p.isPinyin === isPinyin && !p.mustContains,
    );
    if (pools.length === 0) {
      pools = distractorPools.filter((p) => p.isPinyin);
    }
    if (pools.length === 0) {
      pools = distractorPools.filter((p) => p.basicPool);
    }
    if (pools.length === 0) {
      pools = distractorPools;
    }

    pool = pool.concat(...pools.map((p) => p.pool));
  }

  const uniquePool = new Set(pool);
  exclusions.forEach((e) => uniquePool.delete(e));
  pool = [...uniquePool];

  const cards = [...answers];
  while (cards.length < count) {
    // pool should not be empty because having the answers themselves
    const distractor = pool[Math.floor(Math.random() * pool.length)] as string;
    cards.push(distractor);

    // remove confusion letter from pool
    if (confusion1.includes(distractor)) {
      confusion1.forEach((c) => {
        if (c !== distractor) {
          uniquePool.delete(c);
        }
      });
      pool = [...uniquePool];
    }
    if (confusion2.includes(distractor)) {
      confusion2.forEach((c) => {
        if (c !== distractor) {
          uniquePool.delete(c);
        }
      });
      pool = [...uniquePool];
    }
  }

  return cards;
};
